import styled, { css } from 'styled-components';

const NO_ACTION_STYLE = `
  user-select: none;
  pointer-events: none;
`;

export const Wrapper = styled.div`
  margin: 8px 0 24px;
`;

export const SelectWrapper = styled.div`
  position: relative;
`;

export const StyledSelect = styled.select<{ hasError?: boolean; disabled?: boolean }>`
  transition: 0.2s;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  width: 100%;
  height: ${(p) => p.theme.token('height-input')};
  font: ${(p) => p.theme.token('text-style-form-input')};
  margin: 0;
  // Adding an ellipsis overflow instead of letting the select text go under the icon
  padding: 0 calc(12px + 24px + 12px) 0 16px;
  text-overflow: ellipsis;

  /* Do the color and the border color at the same time */
  ${({ hasError, disabled, theme: { token } }) => {
    if (hasError) {
      return `
      color: ${token('text-default')};
      border-color: ${token('border-color-error')};
      `;
    }
    if (disabled) {
      return `
      color: ${token('text-disabled')};
      border-color: ${token('border-color-disabled')};
      `;
    }
    return `
      color: ${token('text-default')};
      border-color: ${token('border-color-input')};
      `;
  }}

  background-color: ${(p) => p.theme.token('background-input')};
  border-radius: ${(p) => p.theme.token('border-radius-input')};
  border-width: ${(p) => p.theme.token('border-width-input')};

  // Removing the default arrow icon
  appearance: none;

  /* Do not change the input on hover when it's disabled */
  :hover {
    ${(p) => !p.disabled && `border-color: ${p.theme.token('border-color-hover')}`};
  }

  :focus {
    outline: none;
    box-shadow: ${(p) => p.theme.token('box-shadow-pattern')};
  }
`;

// This is the label styles when a value is selected
const LABEL_WITH_SELECTED_VALUE_STYLES = css`
  font: ${(p) => p.theme.token('text-style-form-label-value')};
  top: -9px;
  left: 13px;
  right: unset;
  padding: 2px 4px;
  height: min-content;
  width: unset;
`;

export const Label = styled.label<{ hasValue: boolean; hasError?: boolean; disabled?: boolean }>`
  transition: 0.2s;
  position: absolute;
  font: ${(p) => p.theme.token('text-style-form-label-default')};
  margin: 0;
  z-index: 1;
  background: ${(p) => p.theme.token('background-input')};
  border-radius: ${(p) => p.theme.token('border-radius-input')};
  white-space: nowrap;

  color: ${({ hasError, disabled, theme: { token } }) => {
    if (hasError) {
      return token('text-error');
    }
    if (disabled) {
      return token('text-disabled');
    }
    return token('text-default');
  }};
  // If there is no value selected yet and the input state is error, set the color to the default one
  ${({ hasValue, hasError, theme: { token } }) =>
    !hasValue && hasError && `color: ${token('text-default')};`}

  // Centering the input when used as placeholder
  top: 1px;
  bottom: 1px;
  left: 1px;
  margin: 1px;
  display: flex;
  align-items: center;

  // Adding an ellipsis overflow instead of letting the select text go under the icon
  overflow: hidden;
  right: calc(15px + 24px);
  padding-left: 15px;
  text-overflow: ellipsis;
  // This is needed for the overflow:
  // 2px === borders, 16px === left padding, 24px === select icon width, 24px === 12px icon padding x 2
  width: calc(100% - 2px - 16px - 24px - 24px);

  // Animation + styles of the label when there is a selected value
  ${(p) => p.hasValue && LABEL_WITH_SELECTED_VALUE_STYLES}

  ${NO_ACTION_STYLE}
`;

export const IconWrapper = styled.div`
  position: absolute;
  right: 12px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  ${NO_ACTION_STYLE}
`;
